import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDateTime } from "../lib/dates";
import { Search, ScrollText } from "lucide-react";

const ENTITY_TYPES = [
  { key: "all", label: "All types" },
  { key: "grievance", label: "Grievances" },
  { key: "agent", label: "Agents" },
  { key: "user", label: "Users" },
  { key: "option", label: "Options" },
  { key: "color", label: "Colors" },
  { key: "sms", label: "SMS" },
];

const ACTION_STYLES = {
  create: "bg-green-100 text-green-700 border-green-200",
  update: "bg-blue-100 text-blue-700 border-blue-200",
  status: "bg-indigo-100 text-indigo-700 border-indigo-200",
  delete: "bg-red-100 text-red-700 border-red-200",
  option: "bg-amber-100 text-amber-700 border-amber-200",
  color: "bg-purple-100 text-purple-700 border-purple-200",
  sms: "bg-teal-100 text-teal-700 border-teal-200",
  user: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "500" });
    if (entityType !== "all") params.set("entity_type", entityType);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/activity?${params.toString()}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [entityType, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="activity-log-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Master Admin</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
          <ScrollText className="w-7 h-7" /> Activity Log
        </h1>
        <p className="text-sm text-muted-foreground mt-2">A complete audit trail of every change across the tracker.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="rounded-sm h-9 w-[180px]" data-testid="activity-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((t) => (
              <SelectItem key={t.key} value={t.key} data-testid={`activity-type-${t.key}`}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search summary or user…" className="rounded-sm h-9 pl-9" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="activity-count">{rows.length} entries</span>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">When</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Action</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Details</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">No activity found.</TableCell></TableRow>
            )}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} data-testid={`activity-row-${r.id}`} className="border-border">
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">{formatDateTime(r.at)}</TableCell>
                <TableCell className="align-top">
                  <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${ACTION_STYLES[r.action] || "bg-secondary text-foreground border-border"}`}>
                    {r.action}
                  </span>
                  <div className="text-[10px] text-muted-foreground mt-1">{r.entity_type}</div>
                </TableCell>
                <TableCell className="text-sm align-top">{r.summary}</TableCell>
                <TableCell className="text-xs text-muted-foreground align-top whitespace-nowrap">{r.actor_email || r.actor_name || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
